import React from 'react';
import { Mail, Linkedin } from 'lucide-react';
import faithPortrait from '@/assets/partner-faith-wanjiku.jpg';
import SEO from '../../components/SEO';

const FaithWanjiku = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Faith Wanjiku — Law and Code Africa"
        description="Faith Wanjiku at Law and Code Africa. LLB Hons, CPM, Trainee Advocate. Focused on data privacy law, cybersecurity law, and digital compliance."
        path="/partners/faith-wanjiku"
      />

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden bg-primary">
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Faith Wanjiku</h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto mb-8">
              LLB Hons | CPM | Trainee Advocate
            </p>
            <div className="max-w-6xl mx-auto text-lg text-blue-100 space-y-4 text-justify px-4">
              <p>
                Faith Wanjiku is a technology-focused legal practitioner with primary areas of interest in Data Privacy Law, Cybersecurity Law, and Digital Compliance. A Trainee Advocate currently enrolled at the Kenya School of Law, she is committed to building a practice that safeguards digital rights while enabling innovation across African tech ecosystems.
              </p>
              <p>
                She has cultivated expertise in data-protection impact assessments, breach-notification protocols, and cross-border data-transfer agreements. Through conference presentations, policy briefs, and professional networking, she actively contributes to the evolving discourse on technology regulation in Kenya and the wider East African region.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12 mb-16">
          <div className="lg:col-span-1">
            <div className="flex flex-col items-center">
              <div className="relative overflow-hidden rounded-lg shadow-lg w-full max-w-sm mb-6 bg-gray-50">
                <img
                  src={faithPortrait}
                  alt="Faith Wanjiku - Partner, Law and Code Africa"
                  loading="lazy"
                  className="w-full h-auto object-cover transition-all duration-700 hover:scale-110 hover:brightness-110"
                />
              </div>
              <div className="bg-gray-50 p-5 md:p-6 rounded-lg w-full max-w-sm">
                <h3 className="text-lg md:text-xl font-bold text-primary mb-4 text-center">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Mail className="text-secondary flex-shrink-0" size={20} />
                    <span className="text-gray-700 text-sm md:text-base break-all">lawandcodeafrica@gmail.com</span>
                  </div>
                  <div className="flex items-center gap-3 min-w-0">
                    <Linkedin className="text-secondary flex-shrink-0" size={20} />
                    <a
                      href="https://www.linkedin.com/in/faith-wanjiku-law"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline text-sm md:text-base break-all"
                    >
                      Connect on LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h2 className="text-3xl font-bold text-primary mb-8">Background and Qualifications</h2>
            <div className="space-y-6 text-gray-700">
              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Educational Foundation</h3>
                <div className="space-y-3">
                  <p>
                    <strong>Strathmore University, School of Law (LLB Hons):</strong> Attended Strathmore University where she developed expertise in information-technology law, intellectual property, and constitutional litigation. During her tenure, she was a member of the Strathmore Law Review Editorial Board and represented the university at the All-African Moot Court Competition.
                  </p>
                  <p>
                    <strong>Kenya School of Law:</strong> Currently an Advocates Training Programme (ATP) student.
                  </p>
                </div>
              </div>

              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Professional Qualifications</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Certified Professional Mediator</h4>
                    <p className="text-sm text-gray-600">Holds certification as a Professional Mediator</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Data Privacy Specialist</h4>
                    <p className="text-sm text-gray-600">Trained in data-protection impact assessments and compliance frameworks</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Cybersecurity Analyst</h4>
                    <p className="text-sm text-gray-600">Knowledgeable in incident-response protocols and cyber-resilience regulations</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Professional Experience &amp; Community Involvement</h3>
                <div className="space-y-3">
                  <p>
                    <strong>Tech4Justice Initiative:</strong> Volunteers as a Legal Researcher, contributing policy briefs on digital rights, cybersecurity governance, and the regulation of fintech platforms.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Areas of Specialization */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary mb-8 text-center">
            Key Practice Areas and Interests
          </h2>
          <p className="text-lg text-gray-600 text-center mb-10 max-w-4xl mx-auto">
            Faith Wanjiku's professional interests and developing specializations encompass:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Data Privacy Law</h3>
              <p className="text-gray-600">
                Data-protection compliance, cross-border data transfers, consent frameworks, and alignment with the Data Protection Act and GDPR standards.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Cybersecurity Law</h3>
              <p className="text-gray-600">
                Cyber-resilience governance, incident-response obligations, critical-infrastructure protection, and regulatory enforcement under national cybercrime frameworks.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Digital Compliance</h3>
              <p className="text-gray-600">
                Regulatory advisory for fintech, e-commerce platforms, and SaaS providers on licensing, consumer-protection, and data-governance obligations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaithWanjiku;
